import {
	AuthControllerPath,
	TaskExecutorControllerPath,
	urlCombine,
	VerifyMFATokenPath,
} from 'ihub/common';
import AppRoutes from '../enums/app-routes';
import dispatchSnackbar from '../functions/dispatch-snackbar';
import { GetAccessToken, GetTenantID } from '../functions/session-functions';
import HttpResponseOptions from '../interface/http-response-options';
import HttpServices from './http-services';
import Parameter from './query-parameter';

export const coreAPI = urlCombine(import.meta.env.VITE_IHUB_URL, import.meta.env.VITE_IHUB_PREFIX);
export const microServiceAPI = urlCombine(
	import.meta.env.VITE_IHUB_MICROSERVICE_URL,
	import.meta.env.VITE_IHUB_MICROSERVICE_PREFIX
);

export const translationServiceAPI = urlCombine(coreAPI, 'Translation');
export const authAPI = urlCombine(coreAPI, AuthControllerPath);
export const schemaServiceAPI = urlCombine(coreAPI, 'DataSchema');

export const microTaskRunnerAPI = urlCombine(microServiceAPI, TaskExecutorControllerPath);

export const headers = (): HeadersInit => {
	return {
		'Content-Type': 'application/json',
		Authorization: GetAccessToken() ?? '',
	};
};

export const requestMode: RequestMode = 'cors';

export const isURLSet = () => {
	if (!coreAPI || !microServiceAPI) {
		throw new Error('iHub URL has not been configured');
	}
};

export const standardGet = (url: string, additionalHeaders?: HeadersInit) => {
	return fetch(url, {
		method: 'GET',
		headers: { ...headers(), ...additionalHeaders },
		mode: requestMode,
		credentials: 'include',
	}).catch(err => {
		dispatchSnackbar({
			message: 'Network error. Please check your connection.',
			severity: 'error',
		});
		return Promise.reject(err);
	});
};

export const standardPost = (url: string, payload: any, additionalHeaders?: HeadersInit) => {
	return fetch(url, {
		method: 'POST',
		headers: { ...headers(), ...additionalHeaders },
		body: JSON.stringify(payload),
		mode: requestMode,
		credentials: 'include',
	}).catch(err => {
		dispatchSnackbar({
			message: 'Network error. Please check your connection.',
			severity: 'error',
		});
		return Promise.reject(err);
	});
};

export const standardDelete = (
	url: string,
	itemIds?: string[],
	additionalHeaders?: HeadersInit
) => {
	return fetch(url, {
		method: 'DELETE',
		headers: { ...headers(), ...additionalHeaders },
		body: itemIds ? JSON.stringify(itemIds) : undefined,
		mode: requestMode,
		credentials: 'include',
	});
};

export const getTenantID = () => {
	const tenantID = GetTenantID();
	if (!tenantID) {
		throw new Error('The user is not associated with a tenant.');
	}
	return tenantID;
};

export const validateOkResponse = async (
	response: Response,
	request: string,
	objectType: string
) => {
	if (response.status === 200 || response.status === 201) return true;
	const result = await response.clone().json();
	let message = response.statusText;
	if (Object.hasOwn(result, 'message')) {
		message = result.message;
	}
	if (response.status === 401 || response.status === 403) {
		if (request === VerifyMFATokenPath) {
			dispatchSnackbar({
				message: `Invalid OTP.`,
				severity: 'error',
			});
			return;
		}
		if (message === 'MFA has not been completed.') await HttpServices.auth.logout();

		dispatchSnackbar({
			message: message ?? `You do not have permission to ${request} ${objectType}.`,
			severity: 'error',
		});

		if (request === 'save') {
			return;
		} else if (request.includes('get')) {
			window.location.pathname = AppRoutes.Unauthorized();
			return;
		}
	} else if (response.statusText) {
		dispatchSnackbar({
			message: message,
			severity: 'error',
		});
	}
	throw new Error(message);
};

export const handleResponse = async (
	response: Response,
	request: string,
	objectType: string,
	options?: HttpResponseOptions
) => {
	try {
		const isOK = await validateOkResponse(response, request, objectType);
		if (isOK && (response.status === 200 || response.status === 201) && options?.success) {
			const clone = response.clone();
			if (clone.headers.get('Content-Type')?.includes('application/json')) {
				const jsonRes = await response.clone().json();
				options.success(jsonRes);
				return;
			}
			//Leaving this log here since it could affect some other calls unexpectedly
			console.debug(`The body payload is not JSON and we are returning the data as plain text.`);
			options.success(await clone.text());
			return;
		}
	} catch (err) {
		const result = await response.clone().json();
		let message = response.statusText;
		if (Object.hasOwn(result, 'message')) {
			message = result.message;
		}
		if (options?.error) {
			options.error(message ?? err);
		} else {
			dispatchSnackbar({
				message: `Failed ${request} on ${objectType} - ${message}`,
				severity: 'error',
			});
		}
	}
};

export const buildURL = (
	objectType: string,
	pathParameters: string[] = [],
	queryParams?: Parameter[]
) => {
	isURLSet();
	let url = urlCombine(coreAPI, objectType, ...pathParameters);
	if (queryParams && queryParams.length > 0) {
		url += `?` + queryParams.map(x => `${x.key}=${x.value}`).join('&');
	}
	return url;
};
